/**
 * SiteManager Component
 *
 * Standalone site management page - not tied to a specific patient.
 * Replicates pages/3_sites.py from the Streamlit app.
 *
 * Features:
 * - Search sites by name, address, phone, or fax
 * - View/edit site details including diagnostic tests
 * - Manage phone/fax numbers with purposes
 * - View linked providers
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Rating,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Fax as FaxIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { withStyles, ClassNameMap } from '@mui/styles';
import { PatientService } from '../../services/patient.service';
import {
  Site,
  Provider,
  PHONE_PURPOSES,
  TESTS_OUTREACH_OUTCOMES,
  getConfirmationIcon,
  getConfirmationColor,
  formatPhone,
} from '../../model/provider.types';
import { toast } from 'react-toastify';
import Helper from '../../utils/helper';
import { SitePhoneManager } from './SitePhoneManager';
import dashboardStylesheet from '../../styles/dashboard.stylesheet';

const patientService = new PatientService();

// Diagnostic tests available for sites (from Streamlit app)
const DIAGNOSTIC_TESTS = [
  'cardiac MR',
  'cardiac PET',
  'coronary calcium scan',
  'coronary CT angio',
  'dobutamine echo',
  'ECG',
  'nuclear stress test',
  'pulmonary CT angio',
  'TEE',
  'treadmill echo',
  'treadmill stress test',
  'TTE',
  'TTE with bubble study',
  'TTE with strain',
];

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface SiteManagerProps {
  input?: {
    isAddSite?: boolean;
  };
  /** When provided, enables site selection mode with Select buttons */
  onSelectSite?: (site: Site) => void;
  classes: ClassNameMap;
}

const SiteManager: React.FC<SiteManagerProps> = ({ input, onSelectSite, classes: btnClasses }) => {
  const isSelectMode = !!onSelectSite;
  // URL parameter handling
  const location = useLocation();
  const history = useHistory();
  // Tab state
  const [tabIndex, setTabIndex] = useState(input?.isAddSite ? 1 : 0);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Site[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Manual Entry state
  const [manualName, setManualName] = useState('');
  const [manualNpi, setManualNpi] = useState('');
  const [manualTaxId, setManualTaxId] = useState('');
  const [manualAddress1, setManualAddress1] = useState('');
  const [manualAddress2, setManualAddress2] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const [manualZip, setManualZip] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualTests, setManualTests] = useState<string[]>([]);
  const [manualDiagnosticRating, setManualDiagnosticRating] = useState<number | null>(null);
  const [manualIsPlaceholder, setManualIsPlaceholder] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);

  // Multi-phone entry state for Add New Site
  interface PhoneEntry {
    number: string;
    type: 'phone' | 'fax';
    purposes: string[];
  }
  const [manualPhones, setManualPhones] = useState<PhoneEntry[]>([]);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newPhoneType, setNewPhoneType] = useState<'phone' | 'fax'>('phone');
  const [newPhonePurposes, setNewPhonePurposes] = useState<string[]>(['general']);

  // Selected site for detail view
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteProviders, setSiteProviders] = useState<Provider[]>([]);

  // Edit site state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<Site>>({});
  const [editTests, setEditTests] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  // Merge candidates for placeholder sites in detail view
  interface MergeCandidate {
    id: number;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    confidence: number;
    reason: string;
    is_placeholder?: boolean;
  }
  const [detailMergeCandidates, setDetailMergeCandidates] = useState<MergeCandidate[]>([]);
  const [detailMergeCandidatesLoading, setDetailMergeCandidatesLoading] = useState(false);
  const [detailMerging, setDetailMerging] = useState(false);

  // Needs Confirmation state
  const [unconfirmedPhones, setUnconfirmedPhones] = useState<any[]>([]);
  const [unconfirmedLoading, setUnconfirmedLoading] = useState(false);
  const [unconfirmedLoaded, setUnconfirmedLoaded] = useState(false);

  // Deduplication state
  interface DuplicateProvider {
    id: number;
    name: string;
    last_name: string;
    credential?: string;
    specialty?: string;
  }
  interface DuplicateSite extends Site {
    providers?: DuplicateProvider[];
    provider_count?: number;
  }
  interface DuplicateGroup {
    normalized_address: string;
    sites: DuplicateSite[];
    count: number;
    match_reason?: string;  // "Exact address match", "Similar address", "Shared phone/fax"
  }
  const [dedupGroups, setDedupGroups] = useState<DuplicateGroup[]>([]);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupLoaded, setDedupLoaded] = useState(false);

  // Consolidation state - for the currently selected group
  const [consolidatingGroup, setConsolidatingGroup] = useState<DuplicateGroup | null>(null);
  const [winnerId, setWinnerId] = useState<number | null>(null);
  const [mergeSelections, setMergeSelections] = useState<Record<number, boolean>>({});
  const [merging, setMerging] = useState(false);

  // Track if we're editing from consolidation view (so Back returns to consolidation)
  const [returnToConsolidation, setReturnToConsolidation] = useState(false);

  // Placeholder resolution state
  interface PlaceholderStats {
    total: number;
    npi_pattern: number;
    surescripts_pattern: number;
    other: number;
  }
  interface PlaceholderSite extends DuplicateSite {
    extracted_npi?: string;
    has_merge_target?: boolean;
    best_match_confidence?: number | null;
    best_match_name?: string | null;
    best_match_id?: number | null;
    merge_target_count?: number;
  }
  const [placeholderStats, setPlaceholderStats] = useState<PlaceholderStats | null>(null);
  const [placeholderSites, setPlaceholderSites] = useState<PlaceholderSite[]>([]);
  const [placeholderLoading, setPlaceholderLoading] = useState(false);
  const [placeholderLoaded, setPlaceholderLoaded] = useState(false);
  const [placeholderFilter, setPlaceholderFilter] = useState<'all' | 'npi' | 'surescripts' | 'other'>('all');
  const [placeholderSearch, setPlaceholderSearch] = useState('');
  const [totalWithMatches, setTotalWithMatches] = useState(0);
  // Current placeholder being resolved
  const [resolvingPlaceholder, setResolvingPlaceholder] = useState<PlaceholderSite | null>(null);
  const [mergeTargets, setMergeTargets] = useState<any[]>([]);
  const [mergeTargetsLoading, setMergeTargetsLoading] = useState(false);
  // Track if we're editing from placeholder resolution (so Back returns to resolution)
  const [returnToPlaceholder, setReturnToPlaceholder] = useState(false);

  // Track if we came from a provider page (to enable "Back to Provider" navigation)
  const [fromProviderId, setFromProviderId] = useState<number | null>(null);

  // Proximity search state
  interface ProximitySearchResult {
    site: Site;
    distance_miles: number;
    distance_display: string;
    matching_tests: string[];
  }
  const [proximityZipcode, setProximityZipcode] = useState('');
  const [proximityRadius, setProximityRadius] = useState(25);
  const [proximityTests, setProximityTests] = useState<string[]>([]);
  const [proximityRequireAll, setProximityRequireAll] = useState(false);
  const [proximityExcludePlaceholders, setProximityExcludePlaceholders] = useState(true);
  const [proximitySearching, setProximitySearching] = useState(false);
  const [proximityResults, setProximityResults] = useState<ProximitySearchResult[]>([]);
  const [proximityHasSearched, setProximityHasSearched] = useState(false);

  // Bulk Add Candidates state
  interface BulkCheckResult {
    candidate: {
      name: string;
      address?: string;
      city?: string;
      state?: string;
      zip_code?: string;
      phone?: string;
      fax?: string;
    };
    matches: Array<{ site: Site; match_reason: string }>;
    has_matches: boolean;
    action?: 'create' | 'use_existing' | 'skip';
    selected_existing_id?: number;
  }
  const [bulkInput, setBulkInput] = useState('');
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkCheckResult[]>([]);
  const [bulkHasChecked, setBulkHasChecked] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkTestsContext, setBulkTestsContext] = useState<string[]>([]);

  // Helper to detect if query looks like a phone number
  const isPhoneNumber = (query: string): boolean => {
    // Remove common phone formatting characters
    const digitsOnly = query.replace(/[\s\-\(\)\.\+]/g, '');
    // Check if it's mostly digits (at least 7 digits for a phone number)
    return /^\d{7,}$/.test(digitsOnly);
  };

  // ============== Search Sites ==============
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Enter a search term (name, address, phone, or fax)', Helper.bottom_center());
      return;
    }

    try {
      setSearching(true);
      setHasSearched(true);
      setSearchResults([]);

      const query = searchQuery.trim();
      let results: Site[] = [];

      // Check if query looks like a phone number
      if (isPhoneNumber(query)) {
        // Search by phone
        const phoneResponse = await patientService.searchSitesByPhone(query, 50);
        results = phoneResponse.data?.sites || [];
      } else {
        // Search by name
        const nameResponse = await patientService.searchSites({
          query: query,
          limit: 50,
        });
        results = nameResponse.data?.sites || nameResponse.data || [];
      }

      setSearchResults(results);
    } catch (err: any) {
      console.error('Site search failed:', err);
      toast.error('Search failed', Helper.bottom_center());
    } finally {
      setSearching(false);
    }
  };

  // ============== Manual Entry ==============
  const handleAddPhoneEntry = () => {
    if (!newPhoneNumber.trim()) {
      toast.warning('Phone number is required', Helper.bottom_center());
      return;
    }
    setManualPhones([
      ...manualPhones,
      {
        number: newPhoneNumber.trim(),
        type: newPhoneType,
        purposes: newPhonePurposes,
      },
    ]);
    // Reset the add form
    setNewPhoneNumber('');
    setNewPhoneType('phone');
    setNewPhonePurposes(['general']);
  };

  const handleRemovePhoneEntry = (index: number) => {
    setManualPhones(manualPhones.filter((_, i) => i !== index));
  };

  const getSiteIdFromCreateResponse = (responseData: any): number | undefined => {
    return responseData?.id || responseData?.data?.id || responseData?.site?.id || responseData?.data?.site?.id;
  };

  const handleManualCreate = async () => {
    if (!manualName.trim()) {
      toast.warning('Site name is required', Helper.bottom_center());
      return;
    }

    try {
      setManualSaving(true);
      const phoneEntriesToCreate: PhoneEntry[] = [
        ...manualPhones,
        ...(newPhoneNumber.trim()
          ? [{
            number: newPhoneNumber.trim(),
            type: newPhoneType,
            purposes: newPhonePurposes.length > 0 ? newPhonePurposes : ['general'],
          }]
          : []),
      ];

      const response = await patientService.addSite({
        name: manualName.trim(),
        npi: manualNpi.trim() || undefined,
        tax_id: manualTaxId.trim() || undefined,
        address: manualAddress1.trim() || undefined,
        address_2: manualAddress2.trim() || undefined,
        city: manualCity.trim() || undefined,
        state: manualState || undefined,
        zip_code: manualZip.trim() || undefined,
        email: manualEmail.trim() || undefined,
        tests_offered: manualTests.length > 0 ? manualTests.join(', ') : undefined,
        diagnostic_site_rating: manualDiagnosticRating ?? undefined,
        is_placeholder: manualIsPlaceholder,
      });

      const newSiteId = getSiteIdFromCreateResponse(response.data);

      // Add phone/fax entries if any
      if (newSiteId && phoneEntriesToCreate.length > 0) {
        let failedPhoneCount = 0;
        for (const phoneEntry of phoneEntriesToCreate) {
          try {
            await patientService.addSitePhone(newSiteId, {
              number: phoneEntry.number.replace(/\D/g, ''),
              number_type: phoneEntry.type,
              purpose: phoneEntry.purposes.length > 0 ? phoneEntry.purposes.join(', ') : 'general',
            });
          } catch (phoneErr: any) {
            console.error('Failed to add phone:', phoneErr);
            failedPhoneCount++;
            // Continue with other phones even if one fails
          }
        }

        if (failedPhoneCount > 0) {
          toast.warning(`Site created, but ${failedPhoneCount} phone/fax number${failedPhoneCount > 1 ? 's' : ''} failed to save`, Helper.bottom_center());
        }
      } else if (!newSiteId && phoneEntriesToCreate.length > 0) {
        toast.warning('Site created, but phone/fax numbers could not be saved because the new site ID was not returned', Helper.bottom_center());
      }

      toast.success('Site created', Helper.bottom_center());

      // Reset form
      setManualName('');
      setManualNpi('');
      setManualTaxId('');
      setManualAddress1('');
      setManualAddress2('');
      setManualCity('');
      setManualState('');
      setManualZip('');
      setManualEmail('');
      setManualTests([]);
      setManualDiagnosticRating(null);
      setManualIsPlaceholder(false);
      setManualPhones([]);
      setNewPhoneNumber('');
      setNewPhoneType('phone');
      setNewPhonePurposes(['general']);

      if (newSiteId) {
        await loadSite(newSiteId);
      }
    } catch (err: any) {
      toast.error('Failed to create site', Helper.bottom_center());
    } finally {
      setManualSaving(false);
    }
  };

  // ============== Site Detail ==============
  const loadSite = async (siteId: number) => {
    try {
      setSiteLoading(true);
      setSelectedSite(null);
      setSiteProviders([]);
      setEditMode(false);
      setDetailMergeCandidates([]);

      // Load site by ID using dedicated endpoint
      const siteResponse = await patientService.getSite(siteId);
      const site = siteResponse.data;

      if (site) {
        setSelectedSite(site);

        // Load providers linked to this site
        try {
          const providersResponse = await patientService.getSiteProviders(siteId);
          setSiteProviders(providersResponse.data || []);
        } catch (err) {
          console.error('Failed to load site providers:', err);
          setSiteProviders([]);
        }

        // If placeholder, load merge candidates
        if (site.is_placeholder) {
          loadMergeCandidatesForSite(siteId);
        }
      } else {
        toast.error('Site not found', Helper.bottom_center());
      }
    } catch (err: any) {
      console.error('Failed to load site:', err);
      toast.error('Site not found', Helper.bottom_center());
    } finally {
      setSiteLoading(false);
    }
  };

  // Load merge candidates for a placeholder site
  const loadMergeCandidatesForSite = async (siteId: number) => {
    try {
      setDetailMergeCandidatesLoading(true);
      // Pass false to exclude other placeholders - we want real sites to merge into
      const response = await patientService.findSimilarSites(siteId, false);
      const matches = (response.data?.matches || []).filter((m: any) => !m.is_placeholder);
      setDetailMergeCandidates(matches);
    } catch (err: any) {
      console.error('Failed to load merge candidates:', err);
      setDetailMergeCandidates([]);
    } finally {
      setDetailMergeCandidatesLoading(false);
    }
  };

  // Merge current placeholder into a target site
  const handleMergeFromDetail = async (targetSiteId: number) => {
    if (!selectedSite) return;

    try {
      setDetailMerging(true);
      await patientService.mergeSites(selectedSite.id, targetSiteId);
      toast.success('Placeholder merged successfully', Helper.bottom_center());

      // Navigate to the merged target site
      await loadSite(targetSiteId);
    } catch (err: any) {
      console.error('Merge failed:', err);
      toast.error('Failed to merge placeholder', Helper.bottom_center());
    } finally {
      setDetailMerging(false);
    }
  };

  // ============== URL Parameter Handling ==============
  // Load site from URL parameter (e.g., /sites?siteId=123&providerId=456)
  // Skip when in select mode (embedded in a dialog)
  useEffect(() => {
    if (isSelectMode) return;

    const params = new URLSearchParams(location.search);
    const siteIdParam = params.get('siteId');
    const providerIdParam = params.get('providerId');

    // Track if we came from a provider context
    if (providerIdParam) {
      const providerId = parseInt(providerIdParam, 10);
      if (!isNaN(providerId)) {
        setFromProviderId(providerId);
      }
    }

    if (siteIdParam) {
      const id = parseInt(siteIdParam, 10);
      if (!isNaN(id)) {
        loadSite(id);
        // Clear the URL parameter after loading
        history.replace('/sites');
      }
    }
  }, [location.search, isSelectMode]);

  // Navigate back to provider page (when we came from Provider Manager)
  const handleBackToProvider = () => {
    if (fromProviderId) {
      history.push(`/providers?id=${fromProviderId}`);
    }
  };

  const handleBackToList = () => {
    setSelectedSite(null);
    setSiteProviders([]);
    setEditMode(false);
    // If we were editing from consolidation, stay in consolidation view
    if (returnToConsolidation) {
      setReturnToConsolidation(false);
      // consolidatingGroup is still set, so we'll return to consolidation view
    }
    // If we were editing from placeholder resolution, stay in resolution view
    if (returnToPlaceholder) {
      setReturnToPlaceholder(false);
      // resolvingPlaceholder is still set, so we'll return to resolution view
    }
  };

  // Edit a site from consolidation view - keeps consolidation state
  const handleEditFromConsolidation = async (siteId: number) => {
    setReturnToConsolidation(true);
    await loadSite(siteId);
  };

  const handleStartEdit = () => {
    if (!selectedSite) return;
    setEditData({
      name: selectedSite.name || '',
      npi: selectedSite.npi || '',
      address: selectedSite.address || selectedSite.address_1 || '',
      address_2: selectedSite.address_2 || '',
      city: selectedSite.city || '',
      state: selectedSite.state || '',
      zip_code: selectedSite.zip_code || selectedSite.postal_code || '',
      email: selectedSite.email || '',
      is_placeholder: selectedSite.is_placeholder || false,
      comment: selectedSite.comment || '',
      diagnostic_site_rating: selectedSite.diagnostic_site_rating || null,
      tests_outreach_date: selectedSite.tests_outreach_date || null,
      tests_outreach_outcome: selectedSite.tests_outreach_outcome || null,
    });
    // Parse existing tests
    const existingTests = selectedSite.tests_offered
      ? selectedSite.tests_offered.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    setEditTests(existingTests);
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedSite) return;

    try {
      setEditSaving(true);
      const updateData = {
        ...editData,
        tests_offered: editTests.length > 0 ? editTests.join(', ') : null,
      };
      await patientService.updateSite(selectedSite.id, updateData);
      toast.success('Site updated', Helper.bottom_center());
      setEditMode(false);
      await loadSite(selectedSite.id);
    } catch (err: any) {
      toast.error('Failed to update site', Helper.bottom_center());
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData({});
    setEditTests([]);
  };

  // ============== Needs Confirmation ==============
  const loadUnconfirmedPhones = async () => {
    try {
      setUnconfirmedLoading(true);
      // No limit - get all unconfirmed phones
      const response = await patientService.getPhonesNeedingConfirmation();
      const allPhones = response.data?.phones || [];
      // Filter out phones from placeholder sites - no point confirming placeholders
      const nonPlaceholderPhones = allPhones.filter(
        (phone: any) => !phone.site?.is_placeholder
      );
      setUnconfirmedPhones(nonPlaceholderPhones);
      setUnconfirmedLoaded(true);
    } catch (err: any) {
      console.error('Failed to load unconfirmed phones:', err);
      toast.error('Failed to load phones needing confirmation', Helper.bottom_center());
    } finally {
      setUnconfirmedLoading(false);
    }
  };

  const handleConfirmPhone = async (sitePhoneId: number) => {
    try {
      await patientService.confirmSitePhone(sitePhoneId, 'manual');
      // Reload the list
      loadUnconfirmedPhones();
      toast.success('Phone confirmed', Helper.bottom_center());
    } catch (err: any) {
      toast.error('Failed to confirm phone', Helper.bottom_center());
    }
  };

  // Load unconfirmed phones when switching to that tab
  useEffect(() => {
    if (tabIndex === 2 && !unconfirmedLoaded && !unconfirmedLoading) {
      loadUnconfirmedPhones();
    }
  }, [tabIndex, unconfirmedLoaded, unconfirmedLoading]);

  // ============== Deduplication ==============
  const loadDeduplicationData = async () => {
    try {
      setDedupLoading(true);
      setConsolidatingGroup(null);

      // Load duplicate groups (grouped by normalized_address)
      // Use higher limit to match original Streamlit app behavior
      const response = await patientService.getDeduplicationGroups({ limit: 500 });
      setDedupGroups(response.data?.groups || []);
      setDedupLoaded(true);
    } catch (err: any) {
      console.error('Failed to load deduplication data:', err);
      toast.error('Failed to load deduplication data', Helper.bottom_center());
    } finally {
      setDedupLoading(false);
    }
  };

  // Load deduplication data when switching to that tab
  useEffect(() => {
    if (tabIndex === 3 && !dedupLoaded && !dedupLoading) {
      loadDeduplicationData();
    }
  }, [tabIndex, dedupLoaded, dedupLoading]);

  // Start consolidating a group - set up the selection state
  const handleStartConsolidation = (group: DuplicateGroup) => {
    setConsolidatingGroup(group);

    // Default winner: first real site, or first placeholder if no real sites
    const realSites = group.sites.filter(s => !s.is_placeholder);
    const defaultWinner = realSites.length > 0 ? realSites[0].id : group.sites[0].id;
    setWinnerId(defaultWinner);

    // Default: all sites selected for merge
    const selections: Record<number, boolean> = {};
    group.sites.forEach(s => {
      selections[s.id] = true;
    });
    setMergeSelections(selections);
  };

  // Cancel consolidation and go back to list
  const handleCancelConsolidation = () => {
    setConsolidatingGroup(null);
    setWinnerId(null);
    setMergeSelections({});
  };

  // Toggle merge selection for a site
  const handleToggleMergeSelection = (siteId: number) => {
    setMergeSelections(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  // Perform the batch merge - merge all selected losers into the winner
  const handleConsolidateMerge = async () => {
    if (!consolidatingGroup || !winnerId) return;

    // Get losers = selected sites that aren't the winner
    const losers = consolidatingGroup.sites.filter(
      s => s.id !== winnerId && mergeSelections[s.id]
    );

    if (losers.length === 0) {
      toast.warning('No sites selected to merge', Helper.bottom_center());
      return;
    }

    try {
      setMerging(true);

      // Merge each loser into the winner
      let successCount = 0;
      let failCount = 0;

      for (const loser of losers) {
        try {
          await patientService.mergeSites(loser.id, winnerId);
          successCount++;
        } catch (err: any) {
          console.error(`Failed to merge site ${loser.id}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Merged ${successCount} site(s) into winner`, Helper.bottom_center());
      }
      if (failCount > 0) {
        toast.warning(`Failed to merge ${failCount} site(s)`, Helper.bottom_center());
      }

      // Reset and reload
      handleCancelConsolidation();
      setDedupLoaded(false);
      loadDeduplicationData();
    } catch (err: any) {
      console.error('Consolidation failed:', err);
      toast.error('Consolidation failed', Helper.bottom_center());
    } finally {
      setMerging(false);
    }
  };

  // Skip this group (remove from view without merging)
  const handleSkipGroup = (group: DuplicateGroup) => {
    setDedupGroups(dedupGroups.filter(g => g.normalized_address !== group.normalized_address));
    toast.info('Skipped this group', Helper.bottom_center());
  };

  // ============== Placeholder Resolution ==============
  const loadPlaceholderData = async () => {
    try {
      setPlaceholderLoading(true);

      // Load stats and sites in parallel
      const [statsResponse, sitesResponse] = await Promise.all([
        patientService.getPlaceholderStats(),
        patientService.listPlaceholderSites({
          pattern: placeholderFilter === 'all' ? undefined : placeholderFilter,
          search: placeholderSearch || undefined,
          limit: 100,
        }),
      ]);

      setPlaceholderStats(statsResponse.data);
      setPlaceholderSites(sitesResponse.data?.sites || []);
      setTotalWithMatches(sitesResponse.data?.total_with_matches || 0);
      setPlaceholderLoaded(true);
    } catch (err: any) {
      console.error('Failed to load placeholder data:', err);
      toast.error('Failed to load placeholders', Helper.bottom_center());
    } finally {
      setPlaceholderLoading(false);
    }
  };

  // Load placeholder data when switching to that tab
  useEffect(() => {
    if (tabIndex === 4 && !placeholderLoaded && !placeholderLoading) {
      loadPlaceholderData();
    }
  }, [tabIndex, placeholderLoaded, placeholderLoading]);

  // Reload when filter changes
  useEffect(() => {
    if (tabIndex === 4 && placeholderLoaded) {
      loadPlaceholderData();
    }
  }, [placeholderFilter, placeholderSearch]);

  // Start resolving a placeholder
  const handleStartResolution = async (placeholder: PlaceholderSite) => {
    setResolvingPlaceholder(placeholder);
    setMergeTargets([]);

    // Load merge targets (similar non-placeholder sites)
    try {
      setMergeTargetsLoading(true);
      // Pass false to exclude other placeholders - we want real sites to merge into
      const response = await patientService.findSimilarSites(placeholder.id, false);
      setMergeTargets(response.data?.matches || []);
    } catch (err: any) {
      console.error('Failed to load merge targets:', err);
    } finally {
      setMergeTargetsLoading(false);
    }
  };

  // Cancel resolution and go back to list
  const handleCancelResolution = () => {
    setResolvingPlaceholder(null);
    setMergeTargets([]);
  };

  // Edit a placeholder site from resolution view
  const handleEditFromPlaceholder = async (siteId: number) => {
    setReturnToPlaceholder(true);
    await loadSite(siteId);
  };

  // Merge placeholder into a target site
  const handleMergeIntoTarget = async (targetSiteId: number) => {
    if (!resolvingPlaceholder) return;

    try {
      setMerging(true);
      await patientService.mergeSites(resolvingPlaceholder.id, targetSiteId);
      toast.success('Placeholder merged successfully', Helper.bottom_center());

      // Reset and reload
      handleCancelResolution();
      setPlaceholderLoaded(false);
      loadPlaceholderData();
    } catch (err: any) {
      console.error('Merge failed:', err);
      toast.error('Failed to merge placeholder', Helper.bottom_center());
    } finally {
      setMerging(false);
    }
  };

  // Skip this placeholder (move to next)
  const handleSkipPlaceholder = () => {
    if (!resolvingPlaceholder) return;

    // Remove from list and move to next
    const remaining = placeholderSites.filter(s => s.id !== resolvingPlaceholder.id);
    setPlaceholderSites(remaining);

    if (remaining.length > 0) {
      // Auto-start next placeholder
      handleStartResolution(remaining[0]);
    } else {
      handleCancelResolution();
    }

    toast.info('Skipped - moving to next', Helper.bottom_center());
  };

  // Render providers for a site in deduplication view
  const renderSiteProviders = (providers?: DuplicateProvider[]) => {
    if (!providers || providers.length === 0) return null;

    return (
      <Box sx={{ mt: 0.5 }}>
        {providers.slice(0, 3).map((p, i) => (
          <Typography
            key={p.id}
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}
          >
            • {p.last_name}{p.specialty ? ` (${p.specialty})` : ''}
          </Typography>
        ))}
        {providers.length > 3 && (
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
            +{providers.length - 3} more
          </Typography>
        )}
      </Box>
    );
  };

  // ============== Proximity Search ==============
  const handleProximitySearch = async () => {
    if (!proximityZipcode.trim()) {
      toast.warning('Enter a zipcode', Helper.bottom_center());
      return;
    }

    // Validate zipcode format (5 digits)
    const zip = proximityZipcode.trim();
    if (!/^\d{5}$/.test(zip)) {
      toast.error('Enter a valid 5-digit zipcode', Helper.bottom_center());
      return;
    }

    try {
      setProximitySearching(true);
      setProximityHasSearched(true);
      setProximityResults([]);

      const response = await patientService.searchSitesByProximity({
        zipcode: zip,
        radius_miles: proximityRadius,
        tests: proximityTests.length > 0 ? proximityTests : undefined,
        require_all_tests: proximityRequireAll,
        exclude_placeholders: proximityExcludePlaceholders,
        limit: 100,
      });

      // Transform response to ProximitySearchResult
      // Backend returns { site: {...}, distance_miles, distance_display, matching_tests }
      const results: ProximitySearchResult[] = (response.data?.results || []).map((r: any) => ({
        site: r.site,
        distance_miles: r.distance_miles,
        distance_display: r.distance_display || `${r.distance_miles?.toFixed(1)} mi`,
        matching_tests: r.matching_tests || [],
      }));

      setProximityResults(results);
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Search failed';
      toast.error(message, Helper.bottom_center());
    } finally {
      setProximitySearching(false);
    }
  };

  // ============== Bulk Add Candidates ==============

  // Parse bulk input text into candidate objects
  const parseBulkInput = (input: string): Array<{ name: string; address?: string; city?: string; state?: string; zip_code?: string; phone?: string; fax?: string }> => {
    const lines = input.split('\n').filter(line => line.trim());

    // Skip header lines - detect by common header keywords
    const headerKeywords = ['site name', 'address', 'city', 'state', 'zip', 'phone', 'fax', 'location'];
    const isHeaderLine = (line: string): boolean => {
      const lower = line.toLowerCase();
      // If 2+ header keywords found, it's likely a header
      const matchCount = headerKeywords.filter(kw => lower.includes(kw)).length;
      return matchCount >= 2;
    };

    return lines.filter(line => !isHeaderLine(line)).map(line => {
      // Expected format: Site Name | Address | City, State ZIP | Phone | Fax
      const parts = line.split('|').map(p => p.trim());
      const candidate: any = { name: parts[0] || '' };

      if (parts[1]) {
        candidate.address = parts[1];
      }

      if (parts[2]) {
        // Parse "City, State ZIP" or "City, State" or just "City"
        const cityStateZip = parts[2];
        const zipMatch = cityStateZip.match(/(\d{5})(-\d{4})?$/);
        if (zipMatch) {
          candidate.zip_code = zipMatch[1];
          const beforeZip = cityStateZip.slice(0, zipMatch.index).trim();
          const stateMatch = beforeZip.match(/,?\s*([A-Z]{2})$/i);
          if (stateMatch) {
            candidate.state = stateMatch[1].toUpperCase();
            candidate.city = beforeZip.slice(0, stateMatch.index).replace(/,\s*$/, '').trim();
          } else {
            candidate.city = beforeZip;
          }
        } else {
          // No ZIP, try to parse city/state
          const stateMatch = cityStateZip.match(/,?\s*([A-Z]{2})$/i);
          if (stateMatch) {
            candidate.state = stateMatch[1].toUpperCase();
            candidate.city = cityStateZip.slice(0, stateMatch.index).replace(/,\s*$/, '').trim();
          } else {
            candidate.city = cityStateZip;
          }
        }
      }

      if (parts[3]) {
        candidate.phone = parts[3].replace(/[^\d]/g, ''); // Digits only
      }

      if (parts[4]) {
        candidate.fax = parts[4].replace(/[^\d]/g, ''); // Digits only
      }

      return candidate;
    }).filter(c => c.name); // Filter out entries without names
  };

  // Check candidates for duplicates
  const handleBulkCheck = async () => {
    const candidates = parseBulkInput(bulkInput);
    if (candidates.length === 0) {
      toast.warning('No valid candidates found. Check the input format.', Helper.bottom_center());
      return;
    }

    try {
      setBulkChecking(true);
      setBulkHasChecked(true);

      const response = await patientService.bulkCheckSites(candidates);
      const results: BulkCheckResult[] = (response.data?.results || []).map((r: any) => ({
        ...r,
        action: undefined, // User needs to choose
      }));

      setBulkResults(results);
    } catch (error) {
      console.error('Bulk check error:', error);
      toast.error('Failed to check for duplicates', Helper.bottom_center());
    } finally {
      setBulkChecking(false);
    }
  };

  // Handle action selection for a bulk result
  const handleBulkAction = (index: number, action: 'create' | 'use_existing' | 'skip', existingId?: number) => {
    setBulkResults(prev => prev.map((r, i) =>
      i === index ? { ...r, action, selected_existing_id: existingId } : r
    ));
  };

  // Create new sites for selected candidates
  const handleBulkCreate = async () => {
    const toCreate = bulkResults.filter(r => r.action === 'create');
    if (toCreate.length === 0) {
      toast.warning('No sites selected for creation', Helper.bottom_center());
      return;
    }

    try {
      setBulkCreating(true);
      let created = 0;
      let failed = 0;

      for (const result of toCreate) {
        try {
          const siteData: any = {
            name: result.candidate.name,
            address: result.candidate.address,
            city: result.candidate.city,
            state: result.candidate.state,
            zip_code: result.candidate.zip_code,
            tests_offered: bulkTestsContext.length > 0 ? bulkTestsContext.join(', ') : undefined,
            tests_outreach_outcome: 'pending',
            comment: `Added from external search on ${new Date().toLocaleDateString()}`,
          };

          const response = await patientService.createSite(siteData);
          const newSiteId = getSiteIdFromCreateResponse(response.data);
          if (!newSiteId) {
            throw new Error('Created site response did not include an id');
          }

          // Create linked SitePhone records for phone and fax with purpose "general"
          if (result.candidate.phone) {
            try {
              await patientService.addSitePhone(newSiteId, {
                number: result.candidate.phone,
                number_type: 'phone',
                purpose: 'general',
              });
            } catch (phoneErr) {
              console.error('Failed to add phone for site:', result.candidate.name, phoneErr);
            }
          }

          if (result.candidate.fax) {
            try {
              await patientService.addSitePhone(newSiteId, {
                number: result.candidate.fax,
                number_type: 'fax',
                purpose: 'general',
              });
            } catch (faxErr) {
              console.error('Failed to add fax for site:', result.candidate.name, faxErr);
            }
          }

          created++;
        } catch (e) {
          console.error('Failed to create site:', result.candidate.name, e);
          failed++;
        }
      }

      if (created > 0) {
        toast.success(`Created ${created} new site${created > 1 ? 's' : ''}`, Helper.bottom_center());
      }
      if (failed > 0) {
        toast.warning(`Failed to create ${failed} site${failed > 1 ? 's' : ''}`, Helper.bottom_center());
      }

      // Clear the form
      setBulkInput('');
      setBulkResults([]);
      setBulkHasChecked(false);
    } catch (error) {
      console.error('Bulk create error:', error);
      toast.error('Failed to create sites', Helper.bottom_center());
    } finally {
      setBulkCreating(false);
    }
  };

  // ============== Render Functions ==============

  const renderSiteRating = (site: Site) => {
    if (!site.diagnostic_site_rating) return null;

    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Rating
          value={site.diagnostic_site_rating}
          readOnly
          size="small"
          precision={0.5}
          icon={<StarIcon fontSize="inherit" />}
          emptyIcon={<StarBorderIcon fontSize="inherit" />}
        />
        <Typography variant="caption" color="textSecondary">
          {site.diagnostic_site_rating}/5
        </Typography>
      </Stack>
    );
  };

  // Render site card in search results
  const renderSiteCard = (site: Site) => {
    return (
      <Card
        key={site.id}
        variant="outlined"
        sx={{
          mb: 1,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {site.is_placeholder && (
                  <Tooltip title="Placeholder site - needs resolution">
                    <WarningIcon fontSize="small" color="warning" />
                  </Tooltip>
                )}
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={500}>
                  {site.name}
                </Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary" display="block">
                {[site.address || site.address_1, site.city, site.state, site.zip_code || site.postal_code]
                  .filter(Boolean).join(', ') || 'No address'}
              </Typography>
              {renderSiteRating(site)}
            </Box>
            <Stack direction="row" spacing={1}>
              {isSelectMode && (
                <Button
                  size="small"
                  variant="outlined"
                  className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectSite?.(site);
                  }}
                >
                  Select
                </Button>
              )}
              <Button size="small" variant="outlined" className={btnClasses.btn} onClick={() => loadSite(site.id)}>View</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Render provider card in site detail
  const renderProviderCard = (provider: Provider) => {
    const displayName = provider.display_name ||
      `${provider.first_name || ''} ${provider.last_name || ''}`.trim();

    return (
      <Card key={provider.id} variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>
              {displayName}
              {provider.credential && `, ${provider.credential}`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary">
            {provider.specialty || provider.taxonomy_display || 'Unknown specialty'}
            {provider.npi && ` | NPI: ${provider.npi}`}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // Render site detail view
  const renderSiteDetail = () => {
    if (!selectedSite) return null;

    return (
      <Box>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {fromProviderId && (
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToProvider}
              variant="outlined"
              className={btnClasses.btn}
            >
              Back to Provider
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            className={btnClasses.btn}
          >
            {returnToConsolidation ? 'Back to Consolidation' : returnToPlaceholder ? 'Back to Resolution' : 'Back to List'}
          </Button>
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {selectedSite.is_placeholder && (
                  <Tooltip title="Placeholder site - needs resolution">
                    <WarningIcon color="warning" />
                  </Tooltip>
                )}
                <Typography variant="h6">{selectedSite.name}</Typography>
              </Stack>
              <Typography variant="body2" color="textSecondary">
                {[selectedSite.address || selectedSite.address_1, selectedSite.city, selectedSite.state, selectedSite.zip_code || selectedSite.postal_code]
                  .filter(Boolean).join(', ') || 'No address'}
              </Typography>
            </Box>
            {!editMode && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                variant="outlined"
                onClick={handleStartEdit}
                className={btnClasses.btn}
              >
                Edit
              </Button>
            )}
          </Stack>

          {editMode ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Site Name *"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="NPI"
                    value={editData.npi || ''}
                    onChange={(e) => setEditData({ ...editData, npi: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address Line 1"
                    value={editData.address || ''}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address Line 2"
                    value={editData.address_2 || ''}
                    onChange={(e) => setEditData({ ...editData, address_2: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    value={editData.city || ''}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Autocomplete
                    size="small"
                    options={US_STATES}
                    value={editData.state || null}
                    onChange={(_, newValue) => setEditData({ ...editData, state: newValue || '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="State" />
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ZIP Code"
                    value={editData.zip_code || ''}
                    onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={DIAGNOSTIC_TESTS}
                    value={editTests}
                    onChange={(_, newValue) => setEditTests(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Diagnostic Tests Offered"
                        placeholder="Select tests..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                        />
                      ))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editData.is_placeholder || false}
                        onChange={(e) => setEditData({ ...editData, is_placeholder: e.target.checked })}
                      />
                    }
                    label="Mark as placeholder (needs resolution)"
                  />
                </Grid>

                {/* Diagnostic Site Rating & Outreach - only show if site offers tests */}
                {(editTests.length > 0 || selectedSite?.tests_offered) && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                        Diagnostic Site Info
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Site Rating</Typography>
                      <Box>
                        <Rating
                          name="site-rating"
                          value={editData.diagnostic_site_rating || 0}
                          onChange={(_, newValue) => setEditData({ ...editData, diagnostic_site_rating: newValue })}
                          icon={<StarIcon fontSize="inherit" />}
                          emptyIcon={<StarBorderIcon fontSize="inherit" />}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Outreach Date"
                        type="date"
                        value={editData.tests_outreach_date ? editData.tests_outreach_date.split('T')[0] : ''}
                        onChange={(e) => setEditData({ ...editData, tests_outreach_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& input[type="date"]::-webkit-calendar-picker-indicator': {
                            filter: 'var(--calendar-icon-filter, none)',
                            cursor: 'pointer'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Outreach Outcome</InputLabel>
                        <Select
                          value={editData.tests_outreach_outcome || ''}
                          label="Outreach Outcome"
                          onChange={(e) => setEditData({ ...editData, tests_outreach_outcome: e.target.value || null })}
                        >
                          <MenuItem value="">
                            <em>Not contacted</em>
                          </MenuItem>
                          {TESTS_OUTREACH_OUTCOMES.map((outcome) => (
                            <MenuItem key={outcome.value} value={outcome.value}>
                              {outcome.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Notes/Comments */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes"
                    multiline
                    rows={3}
                    value={editData.comment || ''}
                    onChange={(e) => setEditData({ ...editData, comment: e.target.value })}
                    placeholder="Internal notes about this site..."
                  />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} mt={2}>
                <Button size="small" variant="outlined" className={`${btnClasses.btn} ${btnClasses.btnPrimary}`} onClick={handleSaveEdit} disabled={editSaving}>
                  {editSaving ? <CircularProgress size={16} /> : 'Save'}
                </Button>
                <Button size="small" variant="outlined" className={btnClasses.btn} onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {selectedSite.npi && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary">NPI</Typography>
                  <Typography variant="body2">{selectedSite.npi}</Typography>
                </Grid>
              )}
              {selectedSite.email && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Typography variant="body2">{selectedSite.email}</Typography>
                </Grid>
              )}
              {selectedSite.tests_offered && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Diagnostic Tests</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedSite.tests_offered.split(',').map((test, i) => (
                      <Chip key={i} label={test.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                </Grid>
              )}
              {/* Rating and Outreach Status for diagnostic sites */}
              {selectedSite.tests_offered && (
                <>
                  {selectedSite.diagnostic_site_rating && (
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Site Rating</Typography>
                      <Box>
                        <Rating
                          value={selectedSite.diagnostic_site_rating}
                          readOnly
                          size="small"
                          icon={<StarIcon fontSize="inherit" />}
                          emptyIcon={<StarBorderIcon fontSize="inherit" />}
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedSite.tests_outreach_outcome && (
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Outreach Status</Typography>
                      <Box>
                        <Chip
                          size="small"
                          label={TESTS_OUTREACH_OUTCOMES.find(o => o.value === selectedSite.tests_outreach_outcome)?.label || selectedSite.tests_outreach_outcome}
                          color={
                            selectedSite.tests_outreach_outcome === 'confirmed' ? 'success' :
                              selectedSite.tests_outreach_outcome === 'pending' ? 'warning' :
                                selectedSite.tests_outreach_outcome === 'no_longer_offers' ? 'error' :
                                  'default'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedSite.tests_outreach_date && (
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">Outreach Date</Typography>
                      <Typography variant="body2">
                        {new Date(selectedSite.tests_outreach_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              {/* Notes/Comments */}
              {selectedSite.comment && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSite.comment}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>

        {/* Merge Candidates Section - only for placeholder sites */}
        {selectedSite.is_placeholder && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'warning.light', opacity: 0.95 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <WarningIcon color="warning" />
              <Typography variant="subtitle1" fontWeight={500}>
                Resolve Placeholder
              </Typography>
            </Stack>
            <Typography variant="body2" color="textSecondary" mb={2}>
              This is a placeholder site. You can merge it into a real site below, or edit/rename it to convert it to a real site.
            </Typography>

            {detailMergeCandidatesLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">
                  Finding similar sites...
                </Typography>
              </Box>
            ) : detailMergeCandidates.length === 0 ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                No similar sites found. Consider editing this site to add correct information.
              </Alert>
            ) : (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Merge Candidates ({detailMergeCandidates.length}):
                </Typography>
                <Stack spacing={1}>
                  {detailMergeCandidates.map((candidate) => (
                    <Card key={candidate.id} variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <BusinessIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight={500}>
                                {candidate.name}
                              </Typography>
                              <Chip
                                label={`${Math.round(candidate.confidence * 100)}% match`}
                                size="small"
                                color={candidate.confidence >= 0.9 ? 'success' : candidate.confidence >= 0.7 ? 'info' : 'default'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Stack>
                            <Typography variant="caption" color="textSecondary">
                              {candidate.reason} | {[candidate.address, candidate.city, candidate.state].filter(Boolean).join(', ')}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              className={btnClasses.btn}
                              onClick={() => loadSite(candidate.id)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              className={`${btnClasses.btn} ${detailMerging ? btnClasses.disabledBtn : btnClasses.btnPrimary}`}
                              onClick={() => handleMergeFromDetail(candidate.id)}
                              disabled={detailMerging}
                            >
                              {detailMerging ? <CircularProgress size={12} color="inherit" /> : 'Merge'}
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        )}

        {/* Phone/Fax Management Section */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <SitePhoneManager siteId={selectedSite.id} />
        </Paper>

        {/* Linked Providers Section */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            Linked Providers ({siteProviders.length})
          </Typography>
          {siteProviders.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No providers linked to this site
            </Typography>
          ) : (
            siteProviders.map(renderProviderCard)
          )}
        </Paper>
      </Box>
    );
  };

  // ============== Main Render ==============
  if (siteLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (selectedSite) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {renderSiteDetail()}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {!input?.isAddSite && (
        <Typography variant="h5" mb={3}>Site Management</Typography>
      )}

      <Paper sx={{ p: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Search" />
          <Tab label="Add New Site" />
          <Tab label="Phone/Fax Confirmation" />
          <Tab label="Deduplication" />
          <Tab label="Placeholders" />
          <Tab label="Proximity Search" />
          <Tab label="Bulk Add Candidates" />
        </Tabs>

        {/* Tab 1: Search */}
        <TabPanel value={tabIndex} index={0}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Search for sites by name, address, phone, or fax number.
            Click any site to view/edit details.
          </Typography>

          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              label="Search"
              placeholder="Name, address, phone, or fax"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                  handleSearch()
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              startIcon={searching ? <CircularProgress size={12} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Stack>

          {hasSearched && (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Results ({searchResults.length})
              </Typography>
              {searchResults.length > 0 ? (
                searchResults.map(renderSiteCard)
              ) : (
                <Alert severity="info">No matching sites found</Alert>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Tab 2: Add New Site */}
        <TabPanel value={tabIndex} index={1}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Create a new site manually.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                size="small"
                label="Site Name *"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="NPI"
                value={manualNpi}
                onChange={(e) => setManualNpi(e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Tax ID"
                value={manualTaxId}
                onChange={(e) => setManualTaxId(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Address Line 1"
                value={manualAddress1}
                onChange={(e) => setManualAddress1(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Address Line 2"
                value={manualAddress2}
                onChange={(e) => setManualAddress2(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                size="small"
                options={US_STATES}
                value={manualState || null}
                onChange={(_, newValue) => setManualState(newValue || '')}
                renderInput={(params) => (
                  <TextField {...params} label="State" />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="ZIP Code"
                value={manualZip}
                onChange={(e) => setManualZip(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
              />
            </Grid>

            {/* Phone/Fax Numbers Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Phone/Fax Numbers
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                Each number can have multiple purposes.
              </Typography>

              {/* List of added phone entries */}
              {manualPhones.length > 0 && (
                <List dense sx={{ mb: 2 }}>
                  {manualPhones.map((phone, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {phone.type === 'phone' ? (
                          <PhoneIcon fontSize="small" color="action" />
                        ) : (
                          <FaxIcon fontSize="small" color="action" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={formatPhone(phone.number.replace(/\D/g, ''))}
                        secondary={
                          <Box component="span">
                            {phone.purposes.map((p, i) => (
                              <Chip
                                key={i}
                                label={p}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemovePhoneEntry(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Add phone/fax form */}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" fontWeight={500} display="block" sx={{ mb: 1 }}>
                  Add Phone/Fax:
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                  <TextField
                    size="small"
                    label="Number"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    sx={{ width: 160 }}
                  />
                  <FormControl size="small" sx={{ width: 100 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={newPhoneType}
                      label="Type"
                      onChange={(e) => setNewPhoneType(e.target.value as 'phone' | 'fax')}
                    >
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="fax">Fax</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Purpose</InputLabel>
                    <Select
                      multiple
                      value={newPhonePurposes}
                      label="Purpose"
                      onChange={(e) => setNewPhonePurposes(e.target.value as string[])}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          ))}
                        </Box>
                      )}
                    >
                      {PHONE_PURPOSES.map((purpose) => (
                        <MenuItem key={purpose} value={purpose}>
                          {purpose}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    className={btnClasses.btn}
                    startIcon={<AddIcon />}
                    onClick={handleAddPhoneEntry}
                    disabled={!newPhoneNumber.trim()}
                  >
                    Add
                  </Button>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                options={DIAGNOSTIC_TESTS}
                value={manualTests}
                onChange={(_, newValue) => {
                  setManualTests(newValue);
                  if (newValue.length === 0) {
                    setManualDiagnosticRating(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Diagnostic Tests Offered"
                    placeholder="Select tests..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
            {manualTests.length > 0 && (
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="textSecondary">Site Rating</Typography>
                <Box>
                  <Rating
                    name="manual-site-rating"
                    value={manualDiagnosticRating || 0}
                    onChange={(_, newValue) => setManualDiagnosticRating(newValue)}
                    icon={<StarIcon fontSize="inherit" />}
                    emptyIcon={<StarBorderIcon fontSize="inherit" />}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={manualIsPlaceholder}
                    onChange={(e) => setManualIsPlaceholder(e.target.checked)}
                  />
                }
                label="Mark as placeholder (needs resolution)"
              />
            </Grid>
          </Grid>

          <Box mt={2}>
            <Button
              size="small"
              variant="outlined"
              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
              startIcon={manualSaving ? <CircularProgress size={12} /> : <AddIcon />}
              onClick={handleManualCreate}
              disabled={manualSaving || !manualName.trim()}
            >
              Create Site
            </Button>
          </Box>
        </TabPanel>

        {/* Tab 3: Needs Confirmation */}
        <TabPanel value={tabIndex} index={2}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Phone/fax numbers that have never been confirmed or were last confirmed over 1 year ago.
            Confirmation helps ensure contact information is accurate.
          </Typography>

          {unconfirmedLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : unconfirmedPhones.length === 0 ? (
            <Alert severity="success">
              All phone/fax numbers are confirmed!
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                {unconfirmedPhones.length} number{unconfirmedPhones.length !== 1 ? 's' : ''} need confirmation
              </Typography>
              <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                {unconfirmedPhones.map((phone) => (
                  <Card key={phone.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {phone.number_type === 'phone' ? (
                              <Typography variant="body2">📞</Typography>
                            ) : (
                              <Typography variant="body2">📠</Typography>
                            )}
                            <Typography variant="body2" fontWeight={500}>
                              {formatPhone(phone.number)}
                            </Typography>
                            <Chip label={phone.purpose || 'general'} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </Stack>
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => loadSite(phone.site_id)}
                          >
                            {phone.site?.name || `Site #${phone.site_id}`}
                          </Typography>
                          {phone.confirmed_at && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Last confirmed: {new Date(phone.confirmed_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                          onClick={() => handleConfirmPhone(phone.id)}
                        >
                          Confirm
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Tab 4: Deduplication */}
        <TabPanel value={tabIndex} index={3}>
          {consolidatingGroup ? (
            /* Consolidation UI - shown when working on a specific group */
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleCancelConsolidation}
                  className={btnClasses.btn}
                >
                  Back to Groups
                </Button>
                <Typography variant="h6">
                  Consolidate Sites at: {consolidatingGroup.normalized_address || 'Unknown Address'}
                </Typography>
              </Stack>

              <Alert severity="info" sx={{ mb: 2 }}>
                Select a winner (the site to keep) and check which sites should be merged into it.
                Uncheck sites you want to leave alone.
              </Alert>

              {/* Separate real sites from placeholders */}
              {(() => {
                const realSites = consolidatingGroup.sites.filter(s => !s.is_placeholder);
                const placeholderSites = consolidatingGroup.sites.filter(s => s.is_placeholder);

                return (
                  <>
                    {realSites.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Real Sites:</Typography>
                        <Stack spacing={1} mb={2}>
                          {realSites.map((site) => (
                            <Card
                              key={site.id}
                              variant="outlined"
                              sx={{
                                borderColor: winnerId === site.id ? 'success.main' : 'divider',
                                borderWidth: winnerId === site.id ? 2 : 1,
                              }}
                            >
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Checkbox
                                    checked={mergeSelections[site.id] || false}
                                    onChange={() => handleToggleMergeSelection(site.id)}
                                    disabled={winnerId === site.id}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <BusinessIcon fontSize="small" color="action" />
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={() => loadSite(site.id)}
                                      >
                                        {site.name}
                                      </Typography>
                                      {winnerId === site.id && (
                                        <Chip label="WINNER" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                                      )}
                                    </Stack>
                                    <Typography variant="caption" color="textSecondary">
                                      {[site.address_1, site.city, site.state].filter(Boolean).join(', ')}
                                    </Typography>
                                    {site.npi && (
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        NPI: {site.npi}
                                      </Typography>
                                    )}
                                    {renderSiteProviders(site.providers)}
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={btnClasses.btn}
                                      startIcon={<EditIcon />}
                                      onClick={() => handleEditFromConsolidation(site.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={`${btnClasses.btn} ${winnerId === site.id ? btnClasses.btnPrimary : ''}`}
                                      onClick={() => setWinnerId(site.id)}
                                    >
                                      {winnerId === site.id ? 'Winner' : 'Set as Winner'}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      </>
                    )}

                    {placeholderSites.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Placeholder Sites:</Typography>
                        <Stack spacing={1} mb={2}>
                          {placeholderSites.map((site) => (
                            <Card
                              key={site.id}
                              variant="outlined"
                              sx={{
                                borderColor: winnerId === site.id ? 'success.main' : 'divider',
                                borderWidth: winnerId === site.id ? 2 : 1,
                                backgroundColor: 'action.hover',
                              }}
                            >
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Checkbox
                                    checked={mergeSelections[site.id] || false}
                                    onChange={() => handleToggleMergeSelection(site.id)}
                                    disabled={winnerId === site.id}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <WarningIcon fontSize="small" color="warning" />
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={() => loadSite(site.id)}
                                      >
                                        {site.name}
                                      </Typography>
                                      {winnerId === site.id && (
                                        <Chip label="WINNER" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                                      )}
                                    </Stack>
                                    <Typography variant="caption" color="textSecondary">
                                      {[site.address_1, site.city, site.state].filter(Boolean).join(', ')}
                                    </Typography>
                                    {renderSiteProviders(site.providers)}
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={btnClasses.btn}
                                      startIcon={<EditIcon />}
                                      onClick={() => handleEditFromConsolidation(site.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={`${btnClasses.btn} ${winnerId === site.id ? btnClasses.btnPrimary : ''}`}
                                      onClick={() => setWinnerId(site.id)}
                                    >
                                      {winnerId === site.id ? 'Winner' : 'Set as Winner'}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      </>
                    )}
                  </>
                );
              })()}

              {/* Summary and action buttons */}
              <Divider sx={{ my: 2 }} />
              {(() => {
                const winner = consolidatingGroup.sites.find(s => s.id === winnerId);
                const losers = consolidatingGroup.sites.filter(s => s.id !== winnerId && mergeSelections[s.id]);
                const skipped = consolidatingGroup.sites.filter(s => s.id !== winnerId && !mergeSelections[s.id]);

                return (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Summary:</Typography>
                    <Typography variant="body2">
                      <strong>Winner:</strong> {winner?.name || 'None selected'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>To merge ({losers.length}):</strong> {losers.map(s => s.name).join(', ') || 'None'}
                    </Typography>
                    {skipped.length > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        <strong>Skipped ({skipped.length}):</strong> {skipped.map(s => s.name).join(', ')}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={2} mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                        onClick={handleConsolidateMerge}
                        disabled={merging || losers.length === 0}
                        startIcon={merging ? <CircularProgress size={12} /> : null}
                      >
                        {merging ? 'Merging...' : `Merge ${losers.length} Site(s) into Winner`}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        className={btnClasses.btn}
                        onClick={handleCancelConsolidation}
                        disabled={merging}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            /* Group List UI - shown when not consolidating */
            <Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Find and merge duplicate sites. Sites are grouped by matching normalized address.
                Click "Consolidate" to choose a winner and merge sites.
              </Typography>

              <Stack direction="row" spacing={2} mb={2} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                  onClick={() => {
                    setDedupLoaded(false);
                    loadDeduplicationData();
                  }}
                  disabled={dedupLoading}
                  startIcon={dedupLoading ? <CircularProgress size={12} /> : <SearchIcon />}
                >
                  {dedupLoaded ? 'Refresh' : 'Scan for Duplicates'}
                </Button>
                {dedupGroups.length > 0 && (
                  <Chip
                    label={`${dedupGroups.length} duplicate group(s) found`}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>

              {dedupLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : !dedupLoaded ? (
                <Alert severity="info">
                  Click "Scan for Duplicates" to find sites that may be duplicates.
                </Alert>
              ) : dedupGroups.length === 0 ? (
                <Alert severity="success">
                  No duplicate sites found!
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                  {dedupGroups.map((group, groupIndex) => (
                    <Card key={groupIndex} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2">
                                {group.normalized_address || 'Unknown Address'}
                              </Typography>
                              {group.match_reason && (
                                <Chip
                                  label={group.match_reason}
                                  size="small"
                                  color={
                                    group.match_reason === 'Exact address match' ? 'success' :
                                      group.match_reason === 'Similar address' ? 'info' : 'warning'
                                  }
                                  sx={{ height: 18, fontSize: '0.6rem' }}
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="textSecondary">
                              {group.count} sites at this location
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              className={btnClasses.btn}
                              onClick={() => handleSkipGroup(group)}
                            >
                              Skip
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                              onClick={() => handleStartConsolidation(group)}
                            >
                              Consolidate
                            </Button>
                          </Stack>
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        <Stack spacing={1}>
                          {group.sites.map((site) => (
                            <Box
                              key={site.id}
                              sx={{
                                py: 0.5,
                                pl: 1,
                                borderLeft: '2px solid',
                                borderColor: site.is_placeholder ? 'warning.main' : 'grey.300',
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                {site.is_placeholder ? (
                                  <WarningIcon fontSize="small" color="warning" />
                                ) : (
                                  <BusinessIcon fontSize="small" color="action" />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                  onClick={() => loadSite(site.id)}
                                >
                                  {site.name}
                                </Typography>
                                {site.is_placeholder && (
                                  <Chip label="Placeholder" size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                                )}
                              </Stack>
                              {renderSiteProviders(site.providers)}
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Tab 5: Placeholders */}
        <TabPanel value={tabIndex} index={4}>
          {resolvingPlaceholder ? (
            /* Resolution UI - shown when working on a specific placeholder */
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleCancelResolution}
                  className={btnClasses.btn}
                >
                  Back to List
                </Button>
                <Typography variant="h6">
                  Resolve Placeholder
                </Typography>
              </Stack>

              {/* Current placeholder info */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'warning.light', opacity: 0.9 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningIcon color="warning" />
                      <Typography variant="subtitle1" fontWeight={500}>
                        {resolvingPlaceholder.name}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                      {[resolvingPlaceholder.address_1, resolvingPlaceholder.city, resolvingPlaceholder.state]
                        .filter(Boolean).join(', ') || 'No address'}
                    </Typography>
                    {resolvingPlaceholder.phone && (
                      <Typography variant="caption" color="textSecondary">
                        Phone: {formatPhone(resolvingPlaceholder.phone)}
                      </Typography>
                    )}
                    {resolvingPlaceholder.extracted_npi && (
                      <Chip
                        label={`NPI: ${resolvingPlaceholder.extracted_npi}`}
                        size="small"
                        color="info"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                    {renderSiteProviders(resolvingPlaceholder.providers)}
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditFromPlaceholder(resolvingPlaceholder.id)}
                      className={btnClasses.btn}
                    >
                      Edit/Rename
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSkipPlaceholder}
                      className={btnClasses.btn}
                    >
                      Skip
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {/* Resolution options */}
              <Typography variant="subtitle2" gutterBottom>
                Merge into a real site:
              </Typography>

              {mergeTargetsLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : mergeTargets.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No similar sites found. You can edit/rename this placeholder to convert it to a real site.
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                  {/* Separate real sites from placeholders */}
                  {(() => {
                    const realSites = mergeTargets.filter((m: any) => !m.is_placeholder);
                    const otherPlaceholders = mergeTargets.filter((m: any) => m.is_placeholder);

                    return (
                      <>
                        {realSites.length > 0 && (
                          <>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                              Real sites ({realSites.length}):
                            </Typography>
                            <Stack spacing={1} mb={2}>
                              {realSites.map((match: any) => (
                                <Card key={match.id} variant="outlined">
                                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <BusinessIcon fontSize="small" color="action" />
                                          <Typography variant="body2" fontWeight={500}>
                                            {match.name}
                                          </Typography>
                                          <Chip
                                            label={`${Math.round(match.confidence * 100)}% match`}
                                            size="small"
                                            color={match.confidence >= 0.9 ? 'success' : 'info'}
                                            sx={{ height: 18, fontSize: '0.6rem' }}
                                          />
                                        </Stack>
                                        <Typography variant="caption" color="textSecondary">
                                          {match.reason} | {[match.address, match.city, match.state].filter(Boolean).join(', ')}
                                        </Typography>
                                      </Box>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                                        onClick={() => handleMergeIntoTarget(match.id)}
                                        disabled={merging}
                                      >
                                        Merge
                                      </Button>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </>
                        )}

                        {otherPlaceholders.length > 0 && (
                          <>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                              Other placeholders at same location ({otherPlaceholders.length}):
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, fontStyle: 'italic' }}>
                              Consider consolidating these in the Deduplication tab first.
                            </Typography>
                            <Stack spacing={1}>
                              {otherPlaceholders.slice(0, 3).map((match: any) => (
                                <Card key={match.id} variant="outlined" sx={{ backgroundColor: 'action.hover' }}>
                                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <WarningIcon fontSize="small" color="warning" />
                                      <Typography variant="body2">
                                        {match.name}
                                      </Typography>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          </>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
          ) : (
            /* Placeholder List UI - shown when not resolving */
            <Box>
              {/* Stats Dashboard */}
              {placeholderStats && (
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {placeholderStats.total}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total Placeholders
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={2.4}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {totalWithMatches}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        With Matches
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={2.4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: placeholderFilter === 'npi' ? 'action.selected' : undefined,
                      }}
                      onClick={() => setPlaceholderFilter(placeholderFilter === 'npi' ? 'all' : 'npi')}
                    >
                      <Typography variant="h4">
                        {placeholderStats.npi_pattern}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        NPI Pattern
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={2.4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: placeholderFilter === 'surescripts' ? 'action.selected' : undefined,
                      }}
                      onClick={() => setPlaceholderFilter(placeholderFilter === 'surescripts' ? 'all' : 'surescripts')}
                    >
                      <Typography variant="h4">
                        {placeholderStats.surescripts_pattern}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Surescripts
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={2.4}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: placeholderFilter === 'other' ? 'action.selected' : undefined,
                      }}
                      onClick={() => setPlaceholderFilter(placeholderFilter === 'other' ? 'all' : 'other')}
                    >
                      <Typography variant="h4">
                        {placeholderStats.other}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Other
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              <Typography variant="body2" color="textSecondary" mb={2}>
                Resolve placeholder sites by merging into real sites or renaming them.
                Click a stat card above to filter by pattern.
              </Typography>

              <Stack direction="row" spacing={2} mb={2} alignItems="center">
                <TextField
                  size="small"
                  label="Search by name"
                  value={placeholderSearch}
                  onChange={(e) => setPlaceholderSearch(e.target.value)}
                  sx={{ width: 300 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                  onClick={() => {
                    setPlaceholderLoaded(false);
                    loadPlaceholderData();
                  }}
                  disabled={placeholderLoading}
                  startIcon={placeholderLoading ? <CircularProgress size={12} /> : <SearchIcon />}
                >
                  Refresh
                </Button>
                {placeholderFilter !== 'all' && (
                  <Chip
                    label={`Filtered: ${placeholderFilter}`}
                    onDelete={() => setPlaceholderFilter('all')}
                    size="small"
                  />
                )}
              </Stack>

              {placeholderLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : !placeholderLoaded ? (
                <Alert severity="info">
                  Loading placeholders...
                </Alert>
              ) : placeholderSites.length === 0 ? (
                <Alert severity="success">
                  No placeholder sites found! All resolved.
                </Alert>
              ) : (
                <Box>
                  <Typography variant="subtitle2" mb={1}>
                    {placeholderSites.length} placeholder{placeholderSites.length !== 1 ? 's' : ''} found
                  </Typography>
                  <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                    {placeholderSites.map((placeholder) => (
                      <Card key={placeholder.id} variant="outlined" sx={{ mb: 1 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <WarningIcon fontSize="small" color="warning" />
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                  onClick={() => loadSite(placeholder.id)}
                                >
                                  {placeholder.name}
                                </Typography>
                                {placeholder.extracted_npi && (
                                  <Chip
                                    label={`NPI: ${placeholder.extracted_npi}`}
                                    size="small"
                                    color="info"
                                    sx={{ height: 18, fontSize: '0.6rem' }}
                                  />
                                )}
                                {placeholder.has_merge_target && (
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography variant="caption" fontWeight={500}>
                                          Match Found: {placeholder.best_match_name}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Confidence: {Math.round((placeholder.best_match_confidence || 0) * 100)}%
                                          {placeholder.merge_target_count && placeholder.merge_target_count > 1 &&
                                            ` (+${placeholder.merge_target_count - 1} more)`}
                                        </Typography>
                                      </Box>
                                    }
                                  >
                                    <Chip
                                      icon={<CheckCircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                                      label={`${Math.round((placeholder.best_match_confidence || 0) * 100)}%`}
                                      size="small"
                                      color="success"
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Typography variant="caption" color="textSecondary">
                                {[placeholder.address_1, placeholder.city, placeholder.state]
                                  .filter(Boolean).join(', ') || 'No address'}
                              </Typography>
                              {placeholder.has_merge_target && placeholder.best_match_name && (
                                <Typography variant="caption" color="success.main" display="block">
                                  Best match: {placeholder.best_match_name}
                                </Typography>
                              )}
                              {renderSiteProviders(placeholder.providers)}
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                              onClick={() => handleStartResolution(placeholder)}
                            >
                              Resolve
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Tab 6: Proximity Search */}
        <TabPanel value={tabIndex} index={5}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Find sites near a zipcode, optionally filtered by diagnostic tests offered.
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Zipcode"
                placeholder="e.g., 10001"
                value={proximityZipcode}
                onChange={(e) => setProximityZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleProximitySearch()
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                size="small"
                options={[10, 25, 50, 100]}
                value={proximityRadius}
                onChange={(_, v) => setProximityRadius(v || 25)}
                getOptionLabel={(opt) => `${opt} miles`}
                renderInput={(params) => (
                  <TextField {...params} label="Search Radius" />
                )}
                disableClearable
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                size="small"
                options={DIAGNOSTIC_TESTS}
                value={proximityTests}
                onChange={(_, v) => setProximityTests(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Diagnostic Tests" placeholder="Select tests..." />
                )}
                ChipProps={{ size: 'small' }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={proximityRequireAll}
                  onChange={(e) => setProximityRequireAll(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Require all selected tests</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={proximityExcludePlaceholders}
                  onChange={(e) => setProximityExcludePlaceholders(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Exclude placeholders</Typography>}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
              onClick={handleProximitySearch}
              disabled={proximitySearching || !proximityZipcode.trim()}
              startIcon={proximitySearching ? <CircularProgress size={12} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Stack>

          {proximityHasSearched && (
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Results ({proximityResults.length})
              </Typography>
              {proximityResults.length > 0 ? (
                proximityResults.map((result) => (
                  <Card key={result.site.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                              onClick={() => loadSite(result.site.id)}
                            >
                              {result.site.name}
                            </Typography>
                            <Chip
                              label={result.distance_display}
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Stack>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {[result.site.address || result.site.address_1, result.site.city, result.site.state, result.site.zip_code || result.site.postal_code]
                              .filter(Boolean).join(', ') || 'No address'}
                          </Typography>
                          {renderSiteRating(result.site)}
                          {result.matching_tests && result.matching_tests.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {result.matching_tests.map((test) => (
                                <Chip
                                  key={test}
                                  label={test}
                                  size="small"
                                  color="success"
                                  sx={{ mr: 0.5, mb: 0.5, height: 18, fontSize: '0.65rem' }}
                                />
                              ))}
                            </Box>
                          )}
                          {result.site.tests_offered && !result.matching_tests?.length && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="textSecondary">
                                Tests: {result.site.tests_offered}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          {isSelectMode && (
                            <Button
                              size="small"
                              variant="outlined"
                              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                              onClick={() => onSelectSite?.(result.site)}
                            >
                              Select
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            className={btnClasses.btn}
                            onClick={() => loadSite(result.site.id)}
                          >
                            View
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Alert severity="info">
                  No sites found within {proximityRadius} miles of {proximityZipcode}
                  {proximityTests.length > 0 && ` offering ${proximityRequireAll ? 'all' : 'any'} selected tests`}
                </Alert>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Tab 6: Bulk Add Candidates */}
        <TabPanel value={tabIndex} index={6}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Paste candidate sites from external search (GPT, web search, etc.) to check for duplicates and add to the system.
            Each line should be: <code>Site Name | Address | City, State ZIP | Phone | Fax</code> (fax is optional)
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Paste Candidate Sites"
                placeholder={`Example:\nABC Imaging Center | 123 Main St | Austin, TX 78701 | (512) 555-1234 | (512) 555-1235\nXYZ Diagnostics | 456 Oak Ave | San Antonio, TX 78205 | 210-555-5678 |`}
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Optional: Pre-fill Tests</Typography>
              <Autocomplete
                multiple
                size="small"
                options={DIAGNOSTIC_TESTS}
                value={bulkTestsContext}
                onChange={(_, newValue) => setBulkTestsContext(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tests these sites may offer"
                    placeholder="Select tests..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                    />
                  ))
                }
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                New sites will be created with these tests and marked for outreach verification.
              </Typography>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} mt={2} mb={3}>
            <Button
              size="small"
              variant="outlined"
              className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
              onClick={handleBulkCheck}
              disabled={bulkChecking || !bulkInput.trim()}
              startIcon={bulkChecking ? <CircularProgress size={12} /> : <SearchIcon />}
            >
              Check for Duplicates
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={btnClasses.btn}
              onClick={() => {
                setBulkInput('');
                setBulkResults([]);
                setBulkHasChecked(false);
              }}
              disabled={!bulkInput && !bulkHasChecked}
            >
              Clear
            </Button>
          </Stack>

          {bulkHasChecked && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Results ({bulkResults.length} candidates)
              </Typography>

              {bulkResults.length === 0 ? (
                <Alert severity="info">No candidates found. Check the input format.</Alert>
              ) : (
                <>
                  <Stack spacing={2}>
                    {bulkResults.map((result, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={5}>
                              <Typography variant="body2" fontWeight={500}>
                                {result.candidate.name || '(No name)'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {[result.candidate.address, result.candidate.city, result.candidate.state, result.candidate.zip_code]
                                  .filter(Boolean).join(', ')}
                                {result.candidate.phone && ` | Ph: ${result.candidate.phone}`}
                                {result.candidate.fax && ` | Fax: ${result.candidate.fax}`}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              {result.has_matches ? (
                                <Box>
                                  <Chip
                                    icon={<WarningIcon />}
                                    label={`${result.matches.length} possible match${result.matches.length > 1 ? 'es' : ''}`}
                                    color="warning"
                                    size="small"
                                    sx={{ mb: 0.5 }}
                                  />
                                  {result.matches.slice(0, 2).map((match, mi) => (
                                    <Typography key={mi} variant="caption" display="block" color="textSecondary">
                                      • {match.site.name} ({match.match_reason})
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="No matches found"
                                  color="success"
                                  size="small"
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Stack direction="row" spacing={1}>
                                {result.has_matches && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    className={`${btnClasses.btn} ${result.action === 'use_existing' ? btnClasses.btnPrimary : ''}`}
                                    onClick={() => handleBulkAction(index, 'use_existing', result.matches[0]?.site.id)}
                                  >
                                    Use Existing
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  className={`${btnClasses.btn} ${result.action === 'create' ? btnClasses.btnPrimary : ''}`}
                                  onClick={() => handleBulkAction(index, 'create')}
                                >
                                  Create New
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  className={`${btnClasses.btn} ${result.action === 'skip' ? btnClasses.btnPrimary : ''}`}
                                  onClick={() => handleBulkAction(index, 'skip')}
                                >
                                  Skip
                                </Button>
                              </Stack>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={2} mt={3}>
                    <Button
                      size="small"
                      variant="outlined"
                      className={`${btnClasses.btn} ${btnClasses.btnPrimary}`}
                      onClick={handleBulkCreate}
                      disabled={bulkCreating || !bulkResults.some(r => r.action === 'create')}
                      startIcon={bulkCreating ? <CircularProgress size={12} /> : <AddIcon />}
                    >
                      Create {bulkResults.filter(r => r.action === 'create').length} New Sites
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      className={btnClasses.btn}
                      onClick={() => {
                        // Mark all without matches as "create"
                        setBulkResults(prev => prev.map(r => ({
                          ...r,
                          action: r.has_matches ? r.action : 'create'
                        })));
                      }}
                    >
                      Create All Without Matches
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

const SiteManagerStyled = withStyles(dashboardStylesheet)(SiteManager);
export { SiteManagerStyled as SiteManager };
export default SiteManagerStyled;
